import { Injectable, NgZone } from '@angular/core';

import {
  ReadinessGap,
  ReadinessPage,
  ReadinessPerson,
  ReadinessQuery,
  ReadinessRow,
  ReadinessStatus,
  SuburbReadinessService,
} from '../../../../services/suburb-readiness.service';
import { fetchAllPages, gapTarget, readinessCsv } from '../readiness/readiness-rules';
import { saveTextFile } from '../readiness/save-file';
import { apiErrorText } from './api-errors';
import { SuburbCatalogueService } from './catalogue.service';
import { ContentRecordsService } from './content-records.service';
import { inZone } from './in-zone';
import { PageEditorService } from './page-editor.service';
import { PanelNavService } from './panel-nav.service';
import { WorkspaceStateService } from './workspace-state.service';

export interface ReadinessFilters {
  q: string;
  region: string | null;
  /** A sub-region id, or 'none'. */
  subRegion: string | null;
  /** A user id, or 'none'. */
  owner: string | null;
  status: ReadinessStatus | null;
  missing: ReadinessGap[];
  published: 'yes' | 'no' | null;
  kind: 'suburb' | 'subregion' | null;
}

const NO_FILTERS: ReadinessFilters = {
  q: '',
  region: null,
  subRegion: null,
  owner: null,
  status: null,
  missing: [],
  published: null,
  kind: null,
};

/**
 * The Readiness view's state (#262): one row per suburb, place and sub-region page, paged, sorted and filtered on the
 * server; owner and status edited in the row (shown at once, put back if the server refuses); a gap opening the tab
 * that fixes it; and the CSV of every row the filters keep.
 */
@Injectable()
export class ReadinessViewService {
  readonly pageSize = 100;
  filters: ReadinessFilters = { ...NO_FILTERS };
  sort: NonNullable<ReadinessQuery['sort']> = 'name';
  dir: 'asc' | 'desc' = 'asc';
  first = 0;
  rows: ReadinessRow[] = [];
  total = 0;
  meta: ReadinessPage['meta'] | null = null;
  owners: ReadinessPerson[] = [];
  /** The row's owner picker: Nobody, then the owners (built once, not on every redraw of 100 rows). */
  ownerOptions: Array<{ label: string; value: number | null }> = [{ label: 'Nobody', value: null }];
  loading = false;
  exporting = false;
  error = '';

  private requestId = 0;

  constructor(
    private api: SuburbReadinessService,
    private zone: NgZone,
    private state: WorkspaceStateService,
    private catalogue: SuburbCatalogueService,
    private records: ContentRecordsService,
    private nav: PanelNavService,
    private pageEditor: PageEditorService
  ) {}

  /** The view opened: the owners to pick from (once), and the rows. */
  start(): void {
    if (!this.owners.length)
      this.api.owners().subscribe({
        next: inZone(this.zone, (o: ReadinessPerson[]) => this.setOwners(o)),
        error: () => {},
      });
    this.reload();
  }

  private setOwners(owners: ReadinessPerson[]): void {
    this.owners = owners;
    this.ownerOptions = [
      { label: 'Nobody', value: null },
      ...owners.map((o) => ({ label: o.name, value: o.userId })),
    ];
    this.state.redraw();
  }

  /** Filters changed: back to the first page. */
  reload(): void {
    this.first = 0;
    this.load();
  }

  resetFilters(): void {
    this.filters = { ...NO_FILTERS };
    this.reload();
  }

  get anyFilter(): boolean {
    return Object.values(this.filters).some((v) => (Array.isArray(v) ? v.length > 0 : !!v));
  }

  /** The table's page or sort changed. */
  page(first: number, sort?: string, order?: number): void {
    this.first = first;
    if (sort) {
      this.sort = sort as NonNullable<ReadinessQuery['sort']>;
      this.dir = order === -1 ? 'desc' : 'asc';
    }
    this.load();
  }

  private query(): ReadinessQuery {
    return { ...this.filters, sort: this.sort, dir: this.dir };
  }

  private load(): void {
    const id = ++this.requestId;
    this.loading = true;
    this.error = '';
    this.api.list({ ...this.query(), offset: this.first, limit: this.pageSize }).subscribe({
      next: inZone(this.zone, (p: ReadinessPage) => {
        if (id !== this.requestId) return; // a newer request is on its way
        this.rows = p.rows;
        this.total = p.total;
        this.meta = p.meta;
        this.loading = false;
        this.state.redraw();
      }),
      error: inZone(this.zone, (e: unknown) => {
        if (id !== this.requestId) return;
        this.loading = false;
        this.error = apiErrorText(e, "the pages couldn't load");
        this.state.redraw();
      }),
    });
  }

  // ------------------------------------------------------------------ owner and status, in the row

  setOwner(row: ReadinessRow, userId: number | null): void {
    const owner = userId === null ? null : (this.owners.find((o) => o.userId === userId) ?? null);
    this.update(row, { ownerUserId: userId }, { owner });
  }

  setStatus(row: ReadinessRow, status: ReadinessStatus): void {
    this.update(row, { status }, { status });
  }

  /** Shown at once; the server's row replaces it, or the old values come back with the reason. */
  private update(
    row: ReadinessRow,
    change: { ownerUserId?: number | null; status?: ReadinessStatus },
    shown: Partial<ReadinessRow>
  ): void {
    const before = { owner: row.owner, status: row.status, missing: row.missing };
    Object.assign(row, shown);
    this.api.update(row.recordId, change).subscribe({
      next: inZone(this.zone, (next: ReadinessRow) => {
        Object.assign(row, next);
        this.state.redraw();
      }),
      error: inZone(this.zone, (e: unknown) => {
        Object.assign(row, before);
        this.error = `${row.name}: ${apiErrorText(e, "the change wasn't saved")}`;
        this.state.redraw();
      }),
    });
  }

  // ------------------------------------------------------------------ where a gap is fixed

  openGap(row: ReadinessRow, gap: ReadinessGap): void {
    const target = gapTarget(row, gap);
    if (target?.kind === 'suburb') this.nav.openSuburb(target.salCode, target.tab);
    else if (target?.kind === 'subregion') this.nav.openSubRegion(target.id);
    else if (target?.kind === 'record') this.openRow(row);
  }

  /** A row's page: a suburb's Overview, a sub-region's panel, or the page editor of a record not on the map. */
  openRow(row: ReadinessRow): void {
    if (row.template === 'subregion') return this.nav.openSubRegion(row.recordId);
    if (row.salCode) return this.nav.openSuburb(row.salCode, 'overview');
    const record = this.records.recordById(row.recordId);
    if (record) this.pageEditor.editOtherPage(record);
  }

  // ------------------------------------------------------------------ CSV

  /** Every row the filters keep, as a CSV file. */
  exportCsv(): void {
    this.exporting = true;
    fetchAllPages((offset) => this.api.list({ ...this.query(), offset, limit: 200 })).subscribe({
      next: inZone(this.zone, (rows: ReadinessRow[]) => {
        const csv = readinessCsv(rows, {
          region: (id) => this.catalogue.regionName(id),
          subRegion: (id) => this.catalogue.subRegionName(id),
        });
        saveTextFile(`suburb-pages-readiness-${new Date().toISOString().slice(0, 10)}.csv`, csv);
        this.exporting = false;
        this.state.redraw();
      }),
      error: inZone(this.zone, (e: unknown) => {
        this.exporting = false;
        this.error = apiErrorText(e, "the CSV couldn't be built");
        this.state.redraw();
      }),
    });
  }
}
